import {
  PORTAL_PERMISSIONS,
  PORTAL_PERMISSION_LABELS,
  type PortalPermission,
  type PortalUserStatus,
} from '../types/portalUser.js';

// CLIENT PORTAL — editing a portal user's features from the account page.
//
// ⚠️⚠️ FEATURES BELONG TO THE PERSON, NOT TO THE ACCOUNT WHOSE PAGE THEY ARE
// EDITED FROM. `PortalUser.permissions` is one list on the user, and it applies
// to EVERY account their contact links resolve to. The editor sits on one
// account's page, which is exactly why it reads as an account-level switch — and
// it did read that way: Scott spent half an hour on 2026-10-08 looking for a
// per-club "assets" switch that does not exist. So every sentence the editor
// shows says whose setting it is and how many locations it reaches, and lives
// here so it is asserted without jsdom.

/**
 * Whether the editor is offered for this person at all.
 *
 * ⚠️ NOT for a disabled user. A disabled user resolves to an EMPTY scope
 * whatever their permissions say, so a tickable feature list there would change
 * nothing they can see — a control that appears to work and does not. Re-enable
 * them first; their stored features come back with them.
 * ⚠️ An INVITED user IS offered it: their features apply from their first
 * sign-in, and setting them before then is the ordinary case.
 */
export function canEditPermissions(status: PortalUserStatus): boolean {
  return status !== 'disabled';
}

/**
 * The sentence above the checkboxes. Names the person, the count, and that the
 * change is theirs everywhere — never "this account's features".
 */
export function permissionScopeSentence(
  displayName: string,
  locationCount: number,
  accountName: string,
): string {
  if (locationCount === 0) {
    return (
      `These features belong to ${displayName}, not to ${accountName}. ` +
      `${displayName} can't see any locations right now, so this changes nothing ` +
      `until their contact is linked to one.`
    );
  }
  if (locationCount === 1) {
    return (
      `These features belong to ${displayName}, not to ${accountName}. ` +
      `${displayName} can see 1 location, and the change applies there.`
    );
  }
  return (
    `These features belong to ${displayName}, not to ${accountName}. ` +
    `${displayName} can see ${locationCount} locations, and the change applies ` +
    `to all ${locationCount} of them.`
  );
}

/** Order-insensitive, duplicate-insensitive equality, so a tick and untick that
 *  cancel out leave Save disabled rather than writing an empty audit entry. */
export function samePermissions(a: readonly PortalPermission[], b: readonly PortalPermission[]): boolean {
  const sa = new Set(a);
  const sb = new Set(b);
  if (sa.size !== sb.size) return false;
  for (const p of sa) if (!sb.has(p)) return false;
  return true;
}

/** The pending change in words, e.g. "Adds Approved assets · Removes Reports". */
export function permissionChangeSummary(
  before: readonly PortalPermission[],
  after: readonly PortalPermission[],
): string | null {
  const added = PORTAL_PERMISSIONS.filter((p) => after.includes(p) && !before.includes(p));
  const removed = PORTAL_PERMISSIONS.filter((p) => before.includes(p) && !after.includes(p));
  const parts: string[] = [];
  if (added.length) parts.push(`Adds ${added.map((p) => PORTAL_PERMISSION_LABELS[p]).join(', ')}`);
  if (removed.length) parts.push(`Removes ${removed.map((p) => PORTAL_PERMISSION_LABELS[p]).join(', ')}`);
  return parts.length ? parts.join(' · ') : null;
}

/** The toast after a save — repeats the reach, because that is the fact to keep. */
export function permissionSavedMessage(displayName: string, locationCount: number): string {
  if (locationCount === 1) return `Updated ${displayName}'s features for their 1 location.`;
  if (locationCount === 0) return `Updated ${displayName}'s features.`;
  return `Updated ${displayName}'s features across all ${locationCount} locations.`;
}
